import React, {useEffect, useRef, useState} from 'react';
import Card from "../../../components/card";
import {AgGridReact} from "ag-grid-react";
import {LocalizationAgGrid} from "../../../utils/localization-ag-grid";
import TemplateDefault from "../../../components/template-default";
import SelectComponent from "../../../components/select-component";
import InputDateTime from "../../../components/input-datetime";
import {FaList, FaPrint} from "react-icons/fa";
import Button from "../../../components/button";
import reportService from "../../../services/report-service";
import {useQuery} from "@tanstack/react-query";
import projectService from "../../../services/project-service";
import {toast} from "react-toastify";
import {AlertMessage} from "../../../utils/alert-message";
import moment from "moment";
import {print} from "../components/print";
import "../report.css";
import general from "../../../utils/general";

const initialFilters = {
    init: moment().startOf('month').toDate(),
    end: moment().endOf('month').toDate(),
    project: []
}

const HardwareAttendanceTimeReport = () => {
    const [data, setData] = useState([]);
    const [filters, setFilters] = useState(initialFilters);
    const [filtersStr, setFiltersStr] = useState("");
    const handleSelect = (id, value) => setFilters(prev => ({...prev, [id]: value}));
    const handleInput = (value, name) => setFilters(prev => ({...prev, [name]: value}));
    const [pinnedBottomValues, setPinnedBottomValues] = useState({ place: "TOTAL DE CHAMADOS: 0" });

    useEffect(() => {
        document.title = "SISTEMA INTERNO";
    }, []);

    const [columnDefs] = useState([
        {field: "date_initial", headerName: "Abertura", width: 120,
            cellRenderer: params => {
                return general.formatDate(params.value)
            }
        },
        {field: "date_final", headerName: "Encerramento", width: 120,
            cellRenderer: params => {
                return general.formatDate(params.value)
            }
        },
        {field: "attendance_time", headerName: "Tempo de chamado", width: 140,
            cellRenderer: params => {
                return formatDiff(params.value);
            }
        },
        {field: "project", headerName: "Projeto", flex: 1, minWidth: 250},
        {field: "requester", headerName: "Solicitante"},
        {field: "place", headerName: "Produto", flex: 1, minWidth: 250},
    ]);

    const formatDiff = (date) => {
        if (!date) return date;

        let newDateTime = date.replace('days', 'Dias').replace('day', 'Dia');

        const datetime = newDateTime.split(' ');
        if (datetime[1] === 'Dias' || datetime[1] === 'Dia') {
            return newDateTime;
        }

        const time = newDateTime.split(':');
        newDateTime = time[0] + 'h ' + time[1] + 'min';
        return newDateTime;
    }

    const {data: projects} = useQuery({
        queryFn: () => projectService.options().then(({data}) => data),
        queryKey: ["projects"]
    });

    const gridRef = useRef();

    const formatDate = date => {
        if (date) return date.toLocaleDateString() + " " + date.toLocaleTimeString();

        return "";
    }

    useEffect(() => {
        setPinnedBottomValues({place: `TOTAL DE CHAMADOS: ${data.length}`})
    }, [data]);

    const apply = () => {
        const init = formatDate(filters.init);
        const end = formatDate(filters.end);
        const formData = new FormData();

        formData.append("init", init);
        formData.append("end", end);
        formData.append("project", JSON.stringify(filters.project.map(p => p.value)));

        const filtersStr = `Início: ${init} - Fim: ${end}`;
        setFiltersStr(filtersStr);

        reportService.hardwareAttendanceTime(formData)
            .then(({data}) => setData(data))
            .catch((err) => {
                const {data: {message}} = err.response;
                toast.error(AlertMessage[message] ?? "Não foi possível prosseguir.");
            });
    }

    const handlePrint = () => {
        if (data.length === 0) return;

        if (gridRef.current) {
            const title = `RELATÓRIO DE TEMPO DE CHAMADOS`;
            const footer = `<tr><td colspan='5'></td><td>${pinnedBottomValues.place}</td></tr>`
            print(gridRef, columnDefs, title, filtersStr, footer);
        }
    }

    return (
        <TemplateDefault title="Relatório de tempo de chamado">
            <Card>
                <div className="flex flex-col lg:grid grid-cols-1 gap-x-6 gap-y-4 lg:grid-cols-12">
                    <div className={"col-span-2"}>
                        <InputDateTime id={"date-init"} name={"init"} label={"Inicio"} selected={filters.init}
                                       onChange={(value) => handleInput(value, "init")} showTime/>
                    </div>
                    <div className={"col-span-2"}>
                        <InputDateTime id={"date-end"} name={"end"} label={"Fim"} selected={filters.end}
                                       onChange={(value) => handleInput(value, "end")} showTime/>
                    </div>

                    <div className="col-span-4 text-gray-900">
                        <SelectComponent
                            isMulti
                            instanceId="projects"
                            label="Projeto"
                            options={projects ?? []}
                            value={filters.project}
                            onChange={(value) => handleSelect("project", value)}
                            name="projects"
                        />
                    </div>
                    <div className={"hidden lg:block col-span-2"}></div>
                    <Button col={"col-span-1"} onClick={apply}>
                        <FaList/> <span className={"ml-1"}>Aplicar</span>
                    </Button>
                    <Button col={"col-span-1"} onClick={handlePrint}>
                        <FaPrint/>
                    </Button>

                </div>
                <div className={"ag-theme-quartz grid mt-4 report-table"} style={{height: "calc(100vh - 159px)"}}>
                    <AgGridReact
                        ref={gridRef}
                        rowHeight={32}
                        rowData={data}
                        columnDefs={columnDefs}
                        animateRows={false}
                        suppressRowClickSelection={true}
                        suppressCellFocus={true}
                        gridOptions={{localeText: LocalizationAgGrid}}
                        domLayout='autoHeight'
                        pinnedBottomRowData={[pinnedBottomValues]}
                        getRowStyle={(params) => {
                            if (params.node.rowPinned) {
                                return { pointerEvents: "none", fontWeight: "700"};
                            }
                        }}
                    />
                </div>
            </Card>
        </TemplateDefault>
    );
};

export default HardwareAttendanceTimeReport;